/* eslint-disable @typescript-eslint/no-explicit-any */
import MuxVideo from '@mux/mux-video-react';
import cx from 'classnames';
import { useRouter } from 'next/router';
import type { ReactElement, RefObject } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSessionstorageState } from 'rooks';
import shallow from 'zustand/shallow';
import Hls from 'hls.js';
import { delay } from 'lodash-es';

import { sendErrorToServices } from 'lib/logging';
import { VideoData } from 'lib/pages/talks/slug/[...slug].props';

import PlayerOverlay from './InternalPlayer/PlayerOverlay';
import useVideoPlayerStore from './store';
import YouTubePlayer from './YouTubePlayer/youtube';

// Hooks
import { useAds } from './InternalPlayer/hooks/useAds';
import { useBandwidthManagement } from './InternalPlayer/hooks/useBandwidthManagement';
import { useVWOCustomConversionTracking } from './InternalPlayer/hooks/useCustomVWOTracking';
import { useDisplayConditions } from './InternalPlayer/hooks/useDisplayConditions';
import { useErrorHandling } from './InternalPlayer/hooks/useErrorHandling';
import { useFetchSubtitles } from './InternalPlayer/hooks/useFetchSubtitles';
import { useHLSConfig } from './InternalPlayer/hooks/useHLSConfig';
import { useKeyboardControls } from './InternalPlayer/hooks/useKeyboardControls';
import { useLanguageSettings } from './InternalPlayer/hooks/useLanguageSettings';
import { useMixpanelData } from './InternalPlayer/hooks/useMixpanelData';
import { usePlaybackMonitoring } from './InternalPlayer/hooks/usePlaybackMonitoring';
import { usePlayerEvents } from './InternalPlayer/hooks/usePlayerEvents';
import { usePlayerMetadata } from './InternalPlayer/hooks/usePlayerMetadata';
import { useSetup } from './InternalPlayer/hooks/usePlayerSetup';
import { useQualityManagement } from './InternalPlayer/hooks/useQualityManagement';
import { useStreamHostTracking } from './InternalPlayer/hooks/useStreamHostTracking';
import { useVisibilityClasses } from './InternalPlayer/hooks/useVisibilityClasses';

// Components
import AdsContainer from './InternalPlayer/Ads/AdsContainer';

// Utils
import * as styles from './InternalPlayer/Player.styles';
import {
  getUserActivationState,
  isUserActivationSupported
} from './InternalPlayer/utils';

export type Language = {
  endonym: string;
  ianaCode: string;
  isRtl: boolean;
  languageCode: string;
  languageName: string;
};

export type VideoPlayerProps = {
  video: VideoData;
  debug?: boolean;
  hovered?: boolean;
  poster: string;
  hideControls?: boolean;
};

// Attempts to suppress `Can't find variable: disableWebkitEnterFullscreen` errors
// TODO: remove once Telegram has fixed this bug
// https://tedconferences.atlassian.net/browse/WEB-3444
// https://onenr.io/0kjng1lB1Qo
// tl;dr Telegram's webview is throwing an error trying to invoke `disableWebkitEnterFullscreen`
// a non-existent webkit api. This is a workaround to suppress the error and avoid flooding our
// New Relic logs.
if (typeof window !== 'undefined') {
  (window as any).disableWebkitEnterFullscreen =
    (window as any).disableWebkitEnterFullscreen || false;
}

const HLS_QUALITY_LEVELS = {
  '1080p': 10,
  '720p': 8,
  '480p': 6,
  '360p': 5,
  '240p': 2,
  '180p': 0,
  AUTO: -1
};

function VideoPlayer({
  video,
  debug,
  hovered,
  poster,
  hideControls = false
}: VideoPlayerProps): ReactElement {
  const router = useRouter();
  const { query } = router;
  const isEmbed = router.asPath.includes('embed');

  // Player setup
  const {
    isMobile,
    shouldAutoplay,
    mixpanelDistinctId,
    userId,
    playerTalkData,
    service,
    youtubeID,
    muted,
    startTime
  } = useSetup(video);

  // Refs and local state
  const adContainerRef = useRef<HTMLDivElement>(null);
  const [player, setPlayer] = useState<HTMLVideoElement | null>(null);
  const [playerReady, setPlayerReady] = useState(false);
  const hasInitialized = useRef(false);
  const hlsRef = useRef<Hls | null>(null);

  // User activation and mute state
  const userActivationSupported = isUserActivationSupported();
  const userHasBeenActive = getUserActivationState();
  const [isMuted, setIsMuted] = useSessionstorageState(
    'muted_state',
    !userActivationSupported
  );
  const isMutedParam = Boolean(router.query.muted);
  const isMutedParamValue = router.query.muted === 'true';

  // Ads setup
  const { adProps, mainContentSrc, isAdBreak } = useAds({
    player,
    playerTalkData,
    adContainerRef: adContainerRef as RefObject<HTMLDivElement>
  });

  // Store state and actions
  const {
    contentState,
    playbackMode,
    roundedTime: currentTime,
    hasAds,
    onBufferState,
    onDuration,
    onError,
    onPlaybackMode,
    onReady,
    requestedCurrentTime,
    onPause,
    onStreamDuration,
    onStreamHost,
    onTalkComplete,
    onPlay,
    ready,
    requestMute,
    subtitleCues,
    prerollDuration,
    postrollDuration,
    callPause,
    callPlay,
    pauseCalled,
    playCalled,
    requestPlay
  } = useVideoPlayerStore(
    state => ({
      ...state
    }),
    shallow
  );

  const internalPlayer = service === 'internal';

  // Player reference callback
  const playerRef = useCallback((node: HTMLVideoElement | null): void => {
    if (node === null) {
      setPlayer(null);
      return;
    }

    // Only set player if it hasn't been initialized yet
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setPlayer(node);
    }
  }, []);

  // Determine initial muted state based on various conditions
  const getInitialMutedState = useCallback(() => {
    switch (true) {
      case isMutedParam:
        return 'isMutedParam';
      case userActivationSupported:
        return 'hasUserActivation';
      case isMuted:
        return 'isMuted';
      default:
        return 'default';
    }
  }, [isMutedParam, userActivationSupported, isMuted]);

  // Handle play/pause actions
  useEffect(() => {
    if (!player) return;

    if (pauseCalled) {
      callPause(null);
      player.pause();
    }
    if (playCalled) {
      callPlay(null);
      player.play();
    }
  }, [pauseCalled, playCalled, player, callPause, callPlay]);

  // Set muted state helper
  const setMutedState = useCallback(
    (muted: boolean) => {
      if (!player) return;

      player.muted = muted;
      requestMute(muted, false);
      setIsMuted(muted);
    },
    [player, requestMute, setIsMuted]
  );

  // Buffer state handlers
  const handleBufferStart = useCallback(() => {
    if (!player) return;

    if (!player.paused && !player.seeking) {
      onBufferState(true);
    }
  }, [player, onBufferState]);

  const handleBufferEnd = useCallback(
    () => onBufferState(false),
    [onBufferState]
  );

  // Player ready handler
  const handlePlayerReady = useCallback(() => {
    setPlayerReady(true);
  }, []);

  // Handle video ended event
  const handleEnded = useCallback(() => {
    onPlaybackMode('ended');

    if (!hasAds) {
      onTalkComplete();
    }
  }, [hasAds, onPlaybackMode, onTalkComplete]);

  // UI visibility classes
  const { animateVisibleClasses, animateSubtitlePositionClasses } =
    useVisibilityClasses(hovered ?? false, isMobile);

  // Display conditions for controls and subtitles
  const { displayControls, displaySubtitles } = useDisplayConditions({
    internalPlayer,
    playerReady,
    isAdBreak,
    playbackMode,
    contentState,
    subtitleCues
  });

  // Apply all the hooks
  const { togglePlay } = useKeyboardControls({ player });
  useFetchSubtitles(playerTalkData);
  usePlayerEvents(player);
  useVWOCustomConversionTracking(currentTime, video.duration);
  useLanguageSettings(playerTalkData);
  useMixpanelData(video);
  const customHLSConfig = useHLSConfig();
  useQualityManagement(player);

  usePlaybackMonitoring(player);
  useErrorHandling({ player, onError });
  useBandwidthManagement(player);
  useStreamHostTracking({
    player,
    ready,
    mainContentSrc,
    playerTalkData,
    service,
    youtubeID,
    onStreamHost
  });

  // Get player metadata
  const metadata = usePlayerMetadata({
    video,
    isEmbed,
    shouldAutoplay,
    startTime,
    poster,
    userId,
    mixpanelDistinctId,
    hasAds,
    adProps,
    playerTalkData
  });

  // Handle replayed state
  useEffect(() => {
    if (
      playbackMode === 'replayed' &&
      player &&
      prerollDuration !== undefined &&
      playerReady
    ) {
      // Set current time to start of main content
      player.currentTime = prerollDuration;

      // Attempt to play
      player.play().catch(() => {
        // Try with mute if first attempt fails
        setMutedState(true);
        player.play().catch(() => {
          sendErrorToServices(
            'Video Error',
            'Failed to play autoplay video after mute'
          );
          setMutedState(false);
        });
      });

      // Signal that playback has started
      onPlay();
    }
  }, [
    playbackMode,
    player,
    prerollDuration,
    playerReady,
    onPlay,
    setMutedState
  ]);

  // Set ready state and duration when player is ready
  useEffect(() => {
    if (
      playerReady &&
      !adProps.adLoading &&
      internalPlayer &&
      typeof player?.duration === 'number' &&
      player.duration > 0
    ) {
      const postroll = postrollDuration ?? 0;
      const preroll = prerollDuration ?? 0;

      const mainContentDuration = Math.max(
        0,
        player.duration - preroll - postroll
      );

      onReady(true);
      onStreamDuration(player.duration);
      onDuration(Math.floor(mainContentDuration));
    }
  }, [
    adProps.adLoading,
    internalPlayer,
    playerReady,
    contentState,
    player?.duration,
    prerollDuration,
    postrollDuration,
    onReady,
    onStreamDuration,
    onDuration,
    ready
  ]);

  // Set up player event listeners
  useEffect(() => {
    if (player && internalPlayer) {
      player.addEventListener('canplay', handlePlayerReady);
      player.addEventListener('waiting', handleBufferStart);
      player.addEventListener('playing', handleBufferEnd);
      player.addEventListener('canplay', handleBufferEnd);

      return () => {
        player.removeEventListener('canplay', handlePlayerReady);
        player.removeEventListener('waiting', handleBufferStart);
        player.removeEventListener('playing', handleBufferEnd);
        player.removeEventListener('canplay', handleBufferEnd);
      };
    }
  }, [
    player,
    internalPlayer,
    handlePlayerReady,
    handleBufferStart,
    handleBufferEnd
  ]);

  // Handle time seeking
  useEffect(() => {
    if (
      player &&
      requestedCurrentTime !== null &&
      requestedCurrentTime > 0 &&
      ready &&
      contentState === 'content'
    ) {
      // Ensure we have valid duration values
      if (!player.duration || !Number.isFinite(player.duration)) {
        return;
      }

      // Calculate the maximum allowed seek time (before postroll)
      const maxSeekTime = Math.max(
        0,
        player.duration - (postrollDuration ?? 0) - (prerollDuration ?? 0)
      );

      // Adjust the player.currentTime by adding prerollDuration to align with the actual timeline
      const adjustedTime = Math.min(
        requestedCurrentTime + (prerollDuration ?? 0),
        maxSeekTime + (prerollDuration ?? 0)
      );

      if (Number.isFinite(adjustedTime) && adjustedTime >= 0) {
        player.currentTime = adjustedTime;

        if (query.t) {
          delete query.t;
          router.push(
            {
              pathname: router.pathname,
              query
            },
            undefined,
            { shallow: true }
          );
        }
      }
    }
  }, [
    requestedCurrentTime,
    player,
    prerollDuration,
    postrollDuration,
    contentState,
    ready,
    query,
    router
  ]);

  // Initialize playback and mute state when player is ready
  useEffect((): void => {
    if (!player || !internalPlayer || !playerReady || !mainContentSrc) return;

    const initialMutedState = getInitialMutedState();
    let shouldAttemptPlay = false;

    switch (initialMutedState) {
      case 'isMutedParam':
        setMutedState(isMutedParamValue);
        break;
      case 'hasUserActivation':
        if (userActivationSupported && !userHasBeenActive) {
          setMutedState(true);
        } else {
          setMutedState(isMuted);
        }
        break;
      case 'isMuted':
        setMutedState(true);
        break;
      default:
        setMutedState(false);
    }

    // Only attempt to play if not in embed mode or autoplay is enabled
    if (!isEmbed || shouldAutoplay) {
      shouldAttemptPlay = true;
    }

    if (isEmbed && playbackMode === 'playing') {
      requestPlay();
    }

    // Use a microtask to ensure state updates have settled
    if (shouldAttemptPlay && playbackMode !== 'playing') {
      queueMicrotask(() => {
        // Send the requestPlay event to the store
        requestPlay();
        onPlay();
        player.play().catch(() => {
          setMutedState(true);
          player.play().catch(() => {
            sendErrorToServices(
              'Video Error',
              'Failed to play autoplay video after mute'
            );
            setMutedState(false);
          });
        });
      });
    }
  }, [
    playerReady,
    isEmbed,
    shouldAutoplay,
    internalPlayer,
    mainContentSrc,
    getInitialMutedState,
    setMutedState,
    isMutedParamValue,
    userActivationSupported,
    userHasBeenActive,
    isMuted,
    onPlay,
    player
  ]);

  const handleQualityChange = useCallback((level: number) => {
    if (!hlsRef.current) return;

    if (hlsRef.current.currentLevel !== level) {
      hlsRef.current.currentLevel = level;
    } else {
      return;
    }
  }, []);

  // Update HLS initialization effect
  useEffect(() => {
    if (!player) return;
    if (!mainContentSrc) return;

    // Only create a new instance if we don't have one
    if (hlsRef.current) return;

    // Check if HLS is supported
    if (!Hls.isSupported()) return;

    // Create HLS instance with config
    const hlsConfig = customHLSConfig;
    const hlsInstance = new Hls({
      ...hlsConfig,
      debug: debug,
      autoStartLoad: true,
      startLevel: 0
    });

    // Store HLS instance
    hlsRef.current = hlsInstance;

    // Attach media and load source These two methods are synchronous
    hlsInstance.attachMedia(player);
    hlsInstance.loadSource(mainContentSrc as string);

    // Cleanup
    return () => {
      hlsInstance.destroy();
      hlsRef.current = null;
    };
  }, [player, mainContentSrc]); // Only depend on player and URL changes

  useEffect(() => {
    const queryQuality =
      HLS_QUALITY_LEVELS[query?.maxQuality as keyof typeof HLS_QUALITY_LEVELS];

    if (queryQuality !== undefined) {
      delay(() => handleQualityChange(queryQuality), 1000);
    }
  }, [query, handleQualityChange, hlsRef.current?.currentLevel]);

  // Render different player based on service type
  const renderPlayer = useCallback(() => {
    if (!playerTalkData) return null;

    if (service === 'youtube' && youtubeID) {
      return (
        <div className="absolute left-0 top-0 size-full bg-black">
          <YouTubePlayer
            youtubeID={youtubeID}
            onEnded={handleEnded}
            playerTalkData={playerTalkData}
          />
        </div>
      );
    }

    if (service === 'internal') {
      return (
        <MuxVideo
          id="video"
          ref={playerRef}
          slot="media"
          type="hls"
          preload={'auto'}
          muted={muted}
          style={styles.topChromeStyles}
          onPlay={onPlay}
          onClick={togglePlay}
          onPause={onPause}
          onError={onError}
          onEnded={handleEnded}
          playsInline
          controls={false}
          controlsList="nodownload"
          crossOrigin="anonymous"
          envKey={process.env.NEXT_PUBLIC_MUX_ENV_KEY || ''}
          streamType="on-demand"
          preferPlayback="mse"
          className={cx({
            'h-screen w-screen': isEmbed
          })}
          metadata={metadata}
          startTime={startTime ? startTime : 0}
          tabIndex={0}
        />
      );
    }

    return null;
  }, [
    playerTalkData,
    service,
    youtubeID,
    mainContentSrc,
    debug,
    muted,
    onPlay,
    togglePlay,
    onPause,
    onError,
    handleEnded,
    isEmbed,
    metadata,
    customHLSConfig,
    startTime,
    playerRef
  ]);
  return (
    <>
      {!isEmbed && service === 'internal' && (
        <AdsContainer
          adContainerRef={adContainerRef}
          adLoading={adProps.adLoading}
          adError={adProps.adError}
          adTimeRemaining={adProps.adTimeRemaining}
          isAdBreak={isAdBreak}
          isSkipCountdownActive={adProps.isSkipCountdownActive}
          togglePlay={togglePlay}
          playerTalkData={playerTalkData}
        />
      )}

      {renderPlayer()}

      <PlayerOverlay
        displayControls={displayControls}
        displaySubtitles={displaySubtitles}
        hideControls={hideControls}
        isMobile={isMobile}
        animateVisibleClasses={animateVisibleClasses}
        animateSubtitlePositionClasses={animateSubtitlePositionClasses}
        playerTalkData={playerTalkData}
      />
    </>
  );
}

export default memo(VideoPlayer);
